/* eslint-disable */
import { AuctionManager } from './auctionManager'
import { RACE_CIRCUITS } from '@/constants/raceCircuits'

export class OwnerFinancialManager {
  static MIN_SAFE_BALANCE = 10000 // Minimum balance owners want to maintain
  static CRITICAL_BALANCE = 5000  // Balance at which owners take urgent action
  static COMFORTABLE_BALANCE = 25000 // Balance at which owners feel financially secure
  
  static analyzeFinancialPosition(owner, gameStore) {
    const upcomingCosts = this.estimateUpcomingCosts(owner, gameStore)
    const currentLiquidity = owner.money
    const assetsValue = this.calculateAssetsValue(owner, gameStore)
    
    return {
      isStable: currentLiquidity >= this.MIN_SAFE_BALANCE,
      isCritical: currentLiquidity <= this.CRITICAL_BALANCE,
      hasExcessFunds: currentLiquidity >= this.COMFORTABLE_BALANCE,
      liquidityNeeded: Math.max(0, this.MIN_SAFE_BALANCE - currentLiquidity),
      potentialSaleValue: assetsValue,
      upcomingCosts
    }
  }

  static estimateUpcomingCosts(owner, gameStore) {
    const ownedChocobos = gameStore.chocobos.filter(c => 
      owner.chocoboIds.includes(c.id)
    )
    
    // Estimate race entry costs for eligible chocobos
    let estimatedCosts = 0
    ownedChocobos.forEach(chocobo => {
      Object.values(RACE_CIRCUITS).forEach(circuit => {
        if (chocobo.level >= circuit.unlockLevel) {
          // Assume each eligible chocobo might race once in their eligible circuits
          estimatedCosts += circuit.entryFee
        }
      })
    })
    
    return estimatedCosts * 0.5 // Assume 50% of potential races will be entered
  }

  static calculateAssetsValue(owner, gameStore) {
    const ownedChocobos = gameStore.chocobos.filter(c => 
      owner.chocoboIds.includes(c.id)
    )
    
    return ownedChocobos.reduce((total, chocobo) => 
      total + AuctionManager.calculateBaseValue(chocobo), 0
    )
  }

  static decideSaleStrategy(owner, gameStore) {
    const analysis = this.analyzeFinancialPosition(owner, gameStore)
    const ownedChocobos = gameStore.chocobos.filter(c => 
      owner.chocoboIds.includes(c.id)
    )
    
    if (!analysis.isCritical && ownedChocobos.length <= 2) {
      return null // Don't sell if only 2 or fewer chocobos, unless critical
    }

    if (analysis.isCritical) {
      return {
        type: 'auction',
        urgency: 'high',
        minChocobos: Math.ceil(analysis.liquidityNeeded / this.MIN_SAFE_BALANCE),
        markup: 1.2 // 20% markup for urgent sales
      }
    }

    if (!analysis.isStable) {
      return {
        type: 'private_sale',
        urgency: 'medium',
        minChocobos: 1,
        markup: 1.3 // 30% markup for private sales
      }
    }

    return null
  }

  static selectChocobosForSale(owner, gameStore, strategy) {
    if (!strategy) return []
    
    // Extra safety check: never sell chocobos from the player
    if (owner.id === gameStore.playerOwner?.id || owner.isPlayer) {
      console.error('CRITICAL: Attempted to sell player chocobos - blocked!', {
        ownerId: owner.id,
        ownerName: owner.name,
        isPlayer: owner.isPlayer,
        playerOwnerId: gameStore.playerOwner?.id,
        playerOwnerIsPlayer: gameStore.playerOwner?.isPlayer,
        stackTrace: new Error().stack
      })
      return []
    }
    
    const ownedChocobos = gameStore.chocobos.filter(c => 
      owner.chocoboIds.includes(c.id)
    )
    
    // Sort chocobos by various criteria
    const scoredChocobos = ownedChocobos.map(chocobo => {
      const baseValue = AuctionManager.calculateBaseValue(chocobo)
      const potentialValue = baseValue * strategy.markup
      
      // Calculate a "keep score" based on owner's traits and chocobo's attributes
      // Safety check for behavior - use default improvement factor if behavior is missing
      const behaviorFactor = chocobo.behavior?.improvement_factor || 5 // Default middle-tier improvement factor
      
      const keepScore = (
        (chocobo.level * owner.ambition) +
        (behaviorFactor * owner.shrewdness) +
        ((chocobo.hasSpeedXFactor || chocobo.hasStaminaXFactor) ? 50 : 0) +
        (chocobo.level >= 30 ? 100 : 0)
      )

      return {
        chocobo,
        baseValue,
        potentialValue,
        keepScore
      }
    }).sort((a, b) => a.keepScore - b.keepScore) // Sort by ascending keep score

    // Select minimum number of chocobos needed while keeping the best ones
    return scoredChocobos
      .slice(0, strategy.minChocobos)
      .map(scored => ({
        chocobo: scored.chocobo,
        askingPrice: Math.round(scored.potentialValue)
      }))
  }

  static generatePrivateSaleOffers(seller, chocoboForSale, gameStore) {
    const interestedBuyers = gameStore.owners.filter(owner => 
      // Explicitly check if this is the player owner
      owner.id !== gameStore.playerOwner?.id &&
      !owner.isPlayer &&
      owner.id !== seller.id &&
      owner.money >= chocoboForSale.askingPrice &&
      this.analyzeFinancialPosition(owner, gameStore).hasExcessFunds
    )

    return interestedBuyers.map(buyer => {
      const willingnessFactor = (
        (buyer.ambition * 0.4) +
        (buyer.competitiveness * 0.4) +
        (buyer.shrewdness * -0.2)
      ) / 100

      const offerPrice = Math.round(
        chocoboForSale.askingPrice * (0.8 + (willingnessFactor * 0.4))
      )

      return {
        buyer,
        offerPrice
      }
    }).filter(offer => offer.offerPrice >= chocoboForSale.askingPrice * 0.8)
  }

  static processPrivateSale(seller, buyer, chocobo, price, gameStore) {
    // Safety check: never process sales involving the player
    if (seller.id === gameStore.playerOwner?.id || seller.isPlayer) {
      console.error('Attempted to process sale from player - blocked')
      return null
    }
    
    if (buyer.id === gameStore.playerOwner?.id || buyer.isPlayer) {
      console.error('Attempted to process sale to player via private sale - blocked')
      return null
    }

    // Remove chocobo from seller
    seller.chocoboIds = seller.chocoboIds.filter(id => id !== chocobo.id)
    seller.money += price

    // Add chocobo to buyer
    buyer.chocoboIds.push(chocobo.id)
    buyer.money -= price

    return {
      seller,
      buyer,
      chocobo,
      price
    }
  }

  static manageFinances(gameStore) {
    const sales = []
    const auctions = []

    gameStore.owners
      .filter(owner => {
        // Explicitly check if this is the player owner
        if (owner.id === gameStore.playerOwner?.id) return false
        // Also check the isPlayer flag as a backup
        if (owner.isPlayer) return false
        return true
      })
      .forEach(owner => {
        const strategy = this.decideSaleStrategy(owner, gameStore)
        if (!strategy) return

        const chocobosForSale = this.selectChocobosForSale(owner, gameStore, strategy)
        
        if (strategy.type === 'private_sale') {
          chocobosForSale.forEach(({ chocobo, askingPrice }) => {
            const offers = this.generatePrivateSaleOffers(owner, 
              { chocobo, askingPrice }, 
              gameStore
            )

            if (offers.length > 0) {
              // Accept the highest offer
              const bestOffer = offers.sort((a, b) => b.offerPrice - a.offerPrice)[0]
              const sale = this.processPrivateSale(
                owner,
                bestOffer.buyer,
                chocobo,
                bestOffer.offerPrice,
                gameStore
              )
              sales.push(sale)
            } else {
              // If no private buyers, put up for auction
              auctions.push({
                owner,
                chocobo,
                basePrice: askingPrice,
                urgent: strategy.urgency === 'high'
              })
            }
          })
        } else if (strategy.type === 'auction') {
          chocobosForSale.forEach(({ chocobo, askingPrice }) => {
            auctions.push({
              owner,
              chocobo,
              basePrice: askingPrice,
              urgent: strategy.urgency === 'high'
            })
          })
        }
      })

    return {
      sales,
      auctions
    }
  }
}
